import {
    Avatar, Box, Chip, CircularProgress, FormControl, IconButton, InputAdornment,
    MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography
} from "@mui/material"
import { AdminPanelSettingsOutlined, BlockOutlined, Cancel, CheckCircleOutlineOutlined, DeleteOutlineOutlined, PauseCircleOutlineOutlined, Refresh, RemoveModeratorOutlined, Search } from "@mui/icons-material"
import { FC, Fragment, useCallback, useEffect, useState } from "react"
import MenuListPop from "../Common/menu"
import NoData from "../Common/NoData"
import ConfirmPinDialog from "../Common/ConfirmPinDialog"
import { Apis } from "../../helpers"
import { Connect, mapStateToProps } from "../../Redux"
import { TABLE_HEIGHT } from "../../configs"
import { EventsEmit } from "../../configs/events"
import useAccessRole from "../Common/useAccessRole"
import _ from 'lodash'

interface UserRow {
    _id: string
    name: string
    userName: string
    email: string
    phone: string
    dateJoined: string
    status: 'active' | 'inactive' | 'suspended' | 'deleted'
    profilePhotos?: any
    isAdmin?: boolean
}

type StatusFilter = 'all' | 'active' | 'inactive' | 'suspended' | 'deleted'

interface StateProps {
    users: UserRow[]
    loading: boolean
    refreshing: boolean
    searchVal: string
    status: StatusFilter
    page: number
    pageSize: number
    count: number
    sortOrder: 'asc' | 'desc'
    processingId: string | null
    deleteTarget: UserRow | null
    deleting: boolean
    deletePinError: string | null
}

const STATUS_STYLE: Record<string, { color: string; border: string }> = {
    active:    { color: '#2e7d32', border: '#2e7d32' },
    inactive:  { color: '#757575', border: '#bdbdbd' },
    suspended: { color: '#c62828', border: '#c62828' },
    deleted:   { color: '#9e9e9e', border: '#e0e0e0' },
}

const AdminUsers: FC<{ User?: any }> = ({ User }) => {
    // only the super admin can make someone an admin (the server enforces it too)
    const isSuperAdmin = useAccessRole() === 'super'
    const [state, setState] = useState<StateProps>({
        users: [],
        loading: true,
        refreshing: false,
        searchVal: '',
        status: 'all',
        page: 0,
        pageSize: 25,
        count: 0,
        sortOrder: 'desc',
        processingId: null,
        deleteTarget: null,
        deleting: false,
        deletePinError: null
    })

    const {
        users, loading, searchVal, status, page, pageSize, count, refreshing, sortOrder, processingId,
        deleteTarget, deleting, deletePinError
    } = state

    const fetchUsers = ({ searchVal: sv, newStatus, newPage, newPageSize, newSortOrder, processVariable }: any) => {
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        const SortOrder = newSortOrder || state.sortOrder
        const Search = sv !== undefined ? sv : state.searchVal
        const Status = newStatus || state.status
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, page: Page, pageSize: PageSize, sortOrder: SortOrder, searchVal: Search, status: Status }
        Apis({ module: 'Users', action: 'getUsers', searchVal: Search, status: Status, page: Page, pageSize: PageSize, sortOrder: SortOrder })
            .then((resp: any) => {
                setState(s => ({ ...s, users: resp.users || [], count: resp.count ?? s.count, ...afterLoad }))
            })
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    const startSearch = useCallback(_.debounce((val: string) => fetchUsers({ searchVal: val, newPage: 0, processVariable: 'loading' }), 600), [])

    const toggleSort = () => fetchUsers({ newPage: 0, newSortOrder: sortOrder === 'desc' ? 'asc' : 'desc', processVariable: 'loading' })

    const updateStatus = (user: UserRow, newStatus: 'active' | 'inactive' | 'suspended') => {
        setState(s => ({ ...s, processingId: user._id }))
        Apis({ module: 'Users', action: 'updateUserStatus', targetUserId: user._id, status: newStatus })
            .then(() => setState(s => ({
                ...s,
                processingId: null,
                users: s.users.map(u => u._id === user._id ? { ...u, status: newStatus } : u)
            })))
            .catch(() => setState(s => ({ ...s, processingId: null })))
    }

    const setAdmin = (user: UserRow, isAdmin: boolean) => {
        setState(s => ({ ...s, processingId: user._id }))
        Apis({ module: 'Users', action: 'setUserAdmin', targetUserId: user._id, isAdmin })
            .then((resp: any) => {
                EventsEmit('modals', { action: 'toastMsg', type: resp?.error ? 'error' : 'success', message: resp?.message })
                setState(s => ({
                    ...s,
                    processingId: null,
                    users: resp?.error ? s.users : s.users.map(u => u._id === user._id ? { ...u, isAdmin } : u)
                }))
            })
            .catch(() => setState(s => ({ ...s, processingId: null })))
    }

    const runDelete = (pin: string) => {
        if (!deleteTarget) return
        setState(s => ({ ...s, deleting: true, deletePinError: null }))
        Apis({ module: 'Users', action: 'deleteUser', targetUserId: deleteTarget._id, pin })
            .then((resp: any) => {
                if (resp?.error) {
                    setState(s => ({ ...s, deleting: false, deletePinError: resp.message || 'Wrong PIN.' }))
                    return
                }
                setState(s => ({
                    ...s,
                    deleting: false,
                    deleteTarget: null,
                    deletePinError: null,
                    users: s.users.map(u => u._id === deleteTarget._id ? { ...u, status: 'deleted' } : u)
                }))
            })
            .catch(() => setState(s => ({ ...s, deleting: false, deletePinError: 'Something went wrong. Please try again.' })))
    }

    useEffect(() => {
        fetchUsers({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [])

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>Users</Typography>
                    {count > 0 && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {count.toLocaleString()} total
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                            value={status}
                            onChange={(e: SelectChangeEvent) => fetchUsers({ newStatus: e.target.value as StatusFilter, newPage: 0, processVariable: 'loading' })}
                            sx={{ fontSize: 13, borderRadius: 2 }}
                        >
                            <MenuItem sx={{ fontSize: 13 }} value="all">All Statuses</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="active">Active</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="inactive">Inactive</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="suspended">Suspended</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="deleted">Deleted</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        variant="outlined"
                        onChange={e => startSearch(e.target.value)}
                        sx={{ width: 240, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchUsers({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!users.length || loading ?
                <NoData message="No users found." loading={loading} searchVal={searchVal} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Username</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Phone</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>
                                        <TableSortLabel active direction={sortOrder} onClick={toggleSort}>
                                            Date Joined
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map(user => {
                                    const chipStyle = STATUS_STYLE[user.status] || STATUS_STYLE.deleted
                                    return (
                                        <TableRow
                                            hover key={user._id}
                                            sx={{ opacity: processingId === user._id ? 0.4 : 1, transition: 'opacity 0.2s' }}
                                        >
                                            <TableCell>
                                                <Avatar src={user.profilePhotos?.small?.url} sx={{ width: 28, height: 28, fontSize: 12 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                {user.name || '—'}
                                                {user.isAdmin && <Chip label="Admin" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{user.userName || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{user.email || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{user.phone || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                {user.dateJoined
                                                    ? new Date(user.dateJoined).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.status}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        fontSize: 11,
                                                        textTransform: 'capitalize',
                                                        color: chipStyle.color,
                                                        borderColor: chipStyle.border,
                                                        height: 20,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <MenuListPop
                                                    menuList={[
                                                        ...(user.status === 'active'
                                                            ? [
                                                                { label: 'Deactivate', icon: <PauseCircleOutlineOutlined /> },
                                                                { label: 'Suspend', icon: <BlockOutlined /> }
                                                            ]
                                                            : user.status !== 'deleted'
                                                                ? [{ label: 'Activate', icon: <CheckCircleOutlineOutlined /> }]
                                                                : []),
                                                        ...(isSuperAdmin && user.status === 'active' && user._id !== User?._id
                                                            ? [user.isAdmin
                                                                ? { label: 'Remove admin', icon: <RemoveModeratorOutlined /> }
                                                                : { label: 'Make admin', icon: <AdminPanelSettingsOutlined /> }]
                                                            : []),
                                                        ...(isSuperAdmin && user.isAdmin && user.status !== 'active' && user.status !== 'deleted'
                                                            ? [{ label: 'Remove admin', icon: <RemoveModeratorOutlined /> }]
                                                            : []),
                                                        ...(user.status !== 'deleted'
                                                            ? [{ label: 'Delete', icon: <DeleteOutlineOutlined /> }]
                                                            : [])
                                                    ]}
                                                    onSelect={(option: string) => {
                                                        if (option === 'Suspend') updateStatus(user, 'suspended')
                                                        else if (option === 'Deactivate') updateStatus(user, 'inactive')
                                                        else if (option === 'Make admin') setAdmin(user, true)
                                                        else if (option === 'Remove admin') setAdmin(user, false)
                                                        else if (option === 'Delete') setState(s => ({ ...s, deleteTarget: user, deletePinError: null }))
                                                        else updateStatus(user, 'active')
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => fetchUsers({ newPage, newPageSize: pageSize, processVariable: 'loading' })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => fetchUsers({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            }

            {deleteTarget && (
                <ConfirmPinDialog
                    title="Delete User"
                    message={`Enter your PIN to delete ${deleteTarget.name || deleteTarget.userName || 'this user'}. This can be undone by an admin later, but the user will lose access immediately.`}
                    processing={deleting}
                    errorMessage={deletePinError}
                    onConfirm={runDelete}
                    close={() => setState(s => ({ ...s, deleteTarget: null, deletePinError: null }))}
                />
            )}
        </Box>
    )
}

export default Connect(mapStateToProps)(AdminUsers)
